import { Link, LinkProps } from 'react-router-dom-v5-compat';
import { textStyleCss } from 'src/theme/atlas';
import styled, { css } from 'styled-components';

export interface ButtonProps {
  variant: 'primary' | 'secondary' | 'tertiary' | 'error';
  disabled?: boolean;
  $loading?: boolean;
  size?: 'small' | 'medium' | 'large';
  width?: string;
  noTextWrap?: boolean;
  noPadding?: boolean;
  noMinWidth?: boolean;
  darkerBorder?: boolean;
}

export const StyledLink = styled(Link)<ButtonProps & LinkProps>`
  text-decoration: none;

  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  border-radius: 3px;
  ${(props) => !props.noMinWidth && 'min-width: 100px;'}
  ${(props) => props.noTextWrap && 'white-space: nowrap;'}
      border: ${(props) => {
    if (props.disabled && props.variant !== 'tertiary' && !props.$loading) {
      return `1px solid ${props.theme.atlas.semantic.color.action.primary.disabled}`;
    }
    switch (props.variant) {
      case 'secondary':
        return `1px solid ${props.theme.atlas.semantic.color.outline.default}`;
      case 'tertiary':
        return '1px solid transparent';
      case 'error':
        return `1px solid ${props.theme.atlas.semantic.color.action.destructive.default}`;
      default:
        return `1px solid ${props.theme.atlas.semantic.color.action.primary.default}`;
    }
  }};
  ${(props) =>
    props.darkerBorder &&
    `border: 1px solid ${props.theme.atlas.semantic.color.type.muted}`};

  color: ${(props) => {
    if (props.disabled && !props.$loading) {
      if (props.variant === 'primary' || props.variant === 'error') {
        return props.theme.atlas.semantic.color.action.primary
          .onPrimaryDisabled;
      }
      return props.theme.atlas.semantic.color.type.disabled;
    } else if (props.variant === 'primary' || props.variant === 'error') {
      return props.theme.atlas.semantic.color.action.primary.onPrimary;
    } else {
      return props.theme.atlas.semantic.color.action.secondary.onSecondary;
    }
  }};
  width: ${(props) => props.width};
  height: ${(props) => {
    switch (props.size) {
      case 'small':
        return '28px;';
      case 'medium':
        return '40px;';
      case 'large':
        return '56px;';
    }
  }};
  font-size: ${(props) => {
    if (props.size === 'small') {
      return '12px';
    } else if (props.size === 'large') {
      return '18px';
    } else {
      return '14px';
    }
  }};
  padding: ${(props) => {
    if (props.noPadding) {
      return '0px';
    }
    if (props.size === 'small') {
      return '5px 12px';
    } else if (props.size === 'large') {
      return '11px 20px';
    } else {
      return '7px 16px';
    }
  }};
  background-color: ${(props) => {
    switch (props.variant) {
      case 'secondary':
        return props.theme.atlas.semantic.color.surface.default;
      case 'tertiary':
        return props.theme.atlas.semantic.color.surface.default;
      case 'error':
        if (props.disabled) {
          return props.theme.atlas.semantic.color.action.primary.disabled;
        }
        return props.theme.atlas.semantic.color.action.destructive.default;
      default:
        if (props.disabled && !props.$loading) {
          return props.theme.atlas.semantic.color.action.primary.disabled;
        }
        return props.theme.atlas.semantic.color.action.primary.default;
    }
  }};
  pointer-events: ${(props) =>
    props.disabled || props.$loading ? 'none' : 'auto'};
  @media (hover: hover) {
    &:hover {
      cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};

      ${(props) =>
        !props.disabled &&
        css<ButtonProps>`
          background-color: ${(props) => {
            switch (props.variant) {
              case 'primary':
                return props.theme.atlas.semantic.color.action.primary.hover;
              case 'error':
                return props.theme.atlas.semantic.color.action.destructive
                  .hover;
              default:
                return props.theme.atlas.semantic.color.action.secondary
                  .hoverFill;
            }
          }};
        `}
    }
  }
  &:active {
    ${(props) =>
      !props.$loading &&
      css<ButtonProps>`
        background-color: ${(props) => {
          if (props.disabled) {
            return props.theme.atlas.semantic.color.action.primary.disabled;
          }
          switch (props.variant) {
            case 'primary':
              return props.theme.atlas.semantic.color.action.primary.active;
            case 'error':
              return props.theme.atlas.semantic.color.action.destructive.active;
            default:
              return props.theme.atlas.semantic.color.action.secondary
                .activeFill;
          }
        }};
      `}
  }
`;
